import { TimePicker } from '@components/TimePicker';
import { ContentCopyIcon } from '@atgfr8/icons';
import { Box, Checkbox, TextField, Typography } from '@atgfr8/atg-ui-tailwind';
import { SelectInputCustom } from '@components/Select/SelectInputCustom';
import {
  FacilityHoursValues,
  FacilityOperatingHours,
  operatingHoursOrderError,
  operatingTimeForPicker,
  operatingTimeFromPicker,
  preferredAppointmentTimeOrNull,
} from '@common/facilityDetails';
import { preferredAppointmentTimeOptions } from '@views/customer/customerDetail/constants';
import { Index, Show } from 'solid-js';

const days = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

type ScheduleName = 'pickupHours' | 'receivingHours';

const hiddenTimeIconClass =
  '[&_label.input::after]:hidden! [&_label.input::after]:content-none! [&_input[type=time]]:pr-2! [&_input[type=time]::-webkit-calendar-picker-indicator]:hidden!';

type WeeklyOperatingHoursEditorProps<T extends FacilityHoursValues> = {
  facilityDetails: () => T;
  onChange: (facilityDetails: T) => void;
  dataTestIdPrefix: string;
  disabled?: boolean;
  hideTimeIcon?: boolean;
  stacked?: boolean;
};

export const WeeklyOperatingHoursEditor = <T extends FacilityHoursValues>(
  props: WeeklyOperatingHoursEditorProps<T>,
) => {
  const updateDay = (
    schedule: ScheduleName,
    dayOfWeek: number,
    updates: Partial<FacilityOperatingHours>,
  ) => {
    if (props.disabled) return;
    const value = props.facilityDetails();
    props.onChange({
      ...value,
      [schedule]: value[schedule].map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, ...updates } : day,
      ),
    });
  };

  const applyMondayToAll = (schedule: ScheduleName) => {
    if (props.disabled) return;
    const value = props.facilityDetails();
    const monday = value[schedule].find((day) => day.dayOfWeek === 1);
    if (!monday) return;
    props.onChange({
      ...value,
      [schedule]: value[schedule].map((day) => ({
        dayOfWeek: day.dayOfWeek,
        openTime: monday.openTime,
        closeTime: monday.closeTime,
        isClosed: monday.isClosed,
      })),
    });
  };

  const rootClass = () => {
    if (!props.hideTimeIcon) return 'flex flex-col gap-4';
    return `flex flex-col gap-4 ${hiddenTimeIconClass}`;
  };

  const dropOffLocked = () =>
    Boolean(props.disabled) || props.facilityDetails().receivingUsesPickupHours;

  const renderSchedule = (schedule: ScheduleName, label: string) => {
    const locked = () =>
      schedule === 'receivingHours' ? dropOffLocked() : Boolean(props.disabled);
    const mirrorsPickup = () =>
      schedule === 'receivingHours' &&
      props.facilityDetails().receivingUsesPickupHours;
    const hoursForDisplay = () => {
      const details = props.facilityDetails();
      if (mirrorsPickup()) return details.pickupHours;
      return details[schedule];
    };
    const notesForDisplay = () => {
      const details = props.facilityDetails();
      if (schedule === 'pickupHours') return details.pickupHoursNotes;
      if (details.receivingUsesPickupHours) return details.pickupHoursNotes;
      return details.receivingHoursNotes;
    };
    const preferredLabel =
      schedule === 'pickupHours'
        ? 'Preferred Pickup Time'
        : 'Preferred Delivery Time';
    const preferredTestId =
      schedule === 'pickupHours'
        ? `${props.dataTestIdPrefix}-pickup-preferred-appointment-time`
        : `${props.dataTestIdPrefix}-dropoff-preferred-appointment-time`;

    return (
      <div class="flex min-w-0 flex-col rounded-md border border-grayish bg-filter-group-surface p-4">
        <div class="mb-3 flex h-11 shrink-0 flex-nowrap items-center gap-2">
          <Typography variant="body1" class="font-bold text-armstrong-primary">
            {label}
          </Typography>
          <button
            type="button"
            title="Copy Monday to all days"
            aria-label="Copy Monday to all days"
            class="inline-flex size-7 shrink-0 items-center justify-center rounded border border-input-border text-armstrong-primary hover:bg-armstrong-light-blue disabled:cursor-not-allowed disabled:opacity-50"
            disabled={locked()}
            onClick={() => applyMondayToAll(schedule)}
            data-testid={`${props.dataTestIdPrefix}-${schedule}-apply-all`}
          >
            <ContentCopyIcon size={14} />
          </button>
          <Show when={schedule === 'receivingHours'}>
            <Checkbox
              label="Use pickup hours"
              checked={props.facilityDetails().receivingUsesPickupHours}
              disabled={props.disabled}
              onChange={(value: boolean) => {
                if (props.disabled) return;
                props.onChange({
                  ...props.facilityDetails(),
                  receivingUsesPickupHours: value,
                });
              }}
              dataTestId={`${props.dataTestIdPrefix}-receiving-uses-pickup`}
            />
          </Show>
        </div>
        <div class="grid grid-cols-[minmax(4.5rem,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_3.25rem] items-center gap-x-2 gap-y-2">
          <span class="text-xs font-bold text-primary">Day</span>
          <span class="text-xs font-bold text-primary">From</span>
          <span class="text-xs font-bold text-primary">To</span>
          <span class="text-xs font-bold text-primary">Closed</span>
          <Index each={hoursForDisplay()}>
            {(day) => {
              const dayName = days[day().dayOfWeek - 1];
              const closeTimeError = () => {
                const message = operatingHoursOrderError(day());
                if (message == null) return undefined;
                return [message];
              };
              const fieldClass = () => {
                if (closeTimeError() == null) return 'min-w-0';
                return 'min-w-0 self-start';
              };
              return (
                <>
                  <span
                    class="text-sm font-medium text-armstrong-primary"
                    classList={{ 'self-start': closeTimeError() != null }}
                  >
                    {dayName}
                  </span>
                  <TimePicker
                    label=""
                    class={fieldClass()}
                    disabled={locked() || day().isClosed}
                    value={operatingTimeForPicker(day().openTime)}
                    inputProps={{
                      'aria-label': `${label} ${dayName} from`,
                    }}
                    onChange={(value) =>
                      updateDay(schedule, day().dayOfWeek, {
                        openTime: operatingTimeFromPicker(value),
                      })
                    }
                    dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-from`}
                  />
                  <TimePicker
                    label=""
                    disabled={locked() || day().isClosed}
                    value={operatingTimeForPicker(day().closeTime)}
                    inputProps={{
                      'aria-label': `${label} ${dayName} to`,
                    }}
                    onChange={(value) =>
                      updateDay(schedule, day().dayOfWeek, {
                        closeTime: operatingTimeFromPicker(value),
                      })
                    }
                    class={fieldClass()}
                    error={closeTimeError()}
                    dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-to`}
                  />
                  <div classList={{ 'self-start': closeTimeError() != null }}>
                    <Checkbox
                      label=""
                      aria-label={`${label} ${dayName} closed`}
                      checked={day().isClosed}
                      disabled={locked()}
                      onChange={(value: boolean) =>
                        updateDay(
                          schedule,
                          day().dayOfWeek,
                          value
                            ? { isClosed: value, openTime: '', closeTime: '' }
                            : { isClosed: value },
                        )
                      }
                      dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-closed`}
                    />
                  </div>
                </>
              );
            }}
          </Index>
        </div>
        <Box class="mt-4">
          <TextField
            label="Notes"
            fullWidth
            placeholder="Add operating-hour details"
            value={notesForDisplay()}
            disabled={locked()}
            onChange={(value: string) => {
              if (locked()) return;
              if (schedule === 'pickupHours') {
                props.onChange({
                  ...props.facilityDetails(),
                  pickupHoursNotes: value,
                });
                return;
              }
              props.onChange({
                ...props.facilityDetails(),
                receivingHoursNotes: value,
              });
            }}
            maxLength={500}
            dataTestId={
              schedule === 'pickupHours'
                ? `${props.dataTestIdPrefix}-pickup-notes`
                : `${props.dataTestIdPrefix}-receiving-notes`
            }
          />
        </Box>
        <Box class="mt-3">
          <SelectInputCustom
            label={preferredLabel}
            menuItems={preferredAppointmentTimeOptions}
            value={props.facilityDetails().preferredAppointmentTime ?? ''}
            onChange={(value: string) => {
              if (props.disabled) return;
              props.onChange({
                ...props.facilityDetails(),
                preferredAppointmentTime: preferredAppointmentTimeOrNull(value),
              });
            }}
            dataTestId={preferredTestId}
          />
        </Box>
      </div>
    );
  };

  return (
    <div class={rootClass()}>
      <Box class="rounded border border-input-border bg-announcement-reminder-surface px-3 py-2 text-sm text-primary">
        Operating hours are for information only and do not enforce pickup or
        drop-off availability.
      </Box>
      <div
        class={props.stacked ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-3'}
      >
        {renderSchedule('pickupHours', 'Pickup Hours')}
        {renderSchedule('receivingHours', 'Drop Off Hours')}
      </div>
    </div>
  );
};
